import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { dashboardApi, siteVisitsApi } from '../lib/api';
import { inr } from '../mock';
import { useAuth } from '../context/AuthContext';
import { Users, FileText, ArrowUpRight, ArrowRight, PlusCircle, IndianRupee, Target, Sparkles, Zap, Loader2, Calendar, AlertCircle, Phone, MapPin, TrendingUp, Trophy, Clock, Home } from 'lucide-react';
import { toast } from 'sonner';

const statusColor = {
  'Tele Caller': 'bg-sky-100 text-sky-800',
  'Follow-ups': 'bg-cyan-100 text-cyan-800',
  'Admin (Qualify)': 'bg-yellow-100 text-yellow-800',
  'Sales Manager': 'bg-indigo-100 text-indigo-800',
  'Design': 'bg-violet-100 text-violet-800',
  'Proposal Shared': 'bg-purple-100 text-purple-800',
  'Post Proposal Follow-up': 'bg-pink-100 text-pink-800',
  'Deal Won': 'bg-emerald-100 text-emerald-800',
  'Accounts': 'bg-amber-100 text-amber-800',
  'Installation Head - BOS': 'bg-orange-100 text-orange-800',
  'Admin (Procurement)': 'bg-rose-100 text-rose-800',
  'Sales Manager 80%': 'bg-fuchsia-100 text-fuchsia-800',
  'Installation': 'bg-lime-100 text-lime-800',
  'BESCOM Team': 'bg-teal-100 text-teal-800',
  'Handover & Complete': 'bg-green-200 text-green-900',
  'Lost / Dropped': 'bg-slate-200 text-slate-700',
};

const segmentColor = {
  Residential: 'bg-emerald-600',
  Commercial: 'bg-amber-500',
  Industrial: 'bg-sky-500',
};

const stageColor = {
  'New': 'from-sky-500 to-sky-600',
  'Contacted': 'from-indigo-500 to-indigo-600',
  'Site Visit Done': 'from-teal-500 to-teal-600',
  'Quote Sent': 'from-violet-500 to-violet-600',
  'Negotiation': 'from-amber-500 to-amber-600',
  'Won': 'from-emerald-500 to-emerald-600',
  'Lost': 'from-slate-400 to-slate-500',
};

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
const fmtDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Math.round((d - new Date()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  if (diff < 0) return `${Math.abs(diff)}d overdue`;
  return `in ${diff}d`;
};

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState({ overdue: [], today: [], total: 0 });
  const [funnel, setFunnel] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, f, fu, vs] = await Promise.all([
          dashboardApi.stats(),
          dashboardApi.funnel(),
          dashboardApi.followups(),
          siteVisitsApi.list({ upcoming_only: true }),
        ]);
        setStats(s); setFunnel(f); setFollowups(fu); setVisits(vs);
      } catch (e) {
        toast.error('Failed to load dashboard');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !stats) {
    return <div className="py-20 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading dashboard…</div>;
  }

  const firstName = (user?.name || 'Sales').split(' ')[0];
  const kpis = [
    { label: 'Active Leads', value: stats.activeLeads, hint: 'In the pipeline', icon: Users, tone: 'bg-emerald-100 text-emerald-700' },
    { label: 'Proposals Sent', value: stats.proposalsSent, hint: 'Awaiting reply', icon: FileText, tone: 'bg-amber-100 text-amber-700' },
    { label: 'Pipeline Value', value: inr(stats.pipelineValue), hint: 'Across open deals', icon: IndianRupee, tone: 'bg-sky-100 text-sky-700' },
    { label: 'Win Rate', value: (funnel?.overall_win_pct ?? stats.winRate) + '%', hint: `Cycle ${funnel?.avg_cycle_days ?? '—'}d avg`, icon: Target, tone: 'bg-violet-100 text-violet-700' },
  ];
  const totalFunnel = Math.max(1, ...stats.funnel.map(f => f.count));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-emerald-950">Good morning, {firstName} ☀</h1>
          <p className="text-slate-600 mt-1">{followups.total > 0 ? `You have ${followups.total} follow-up${followups.total > 1 ? 's' : ''} due today${followups.overdue.length ? ` (${followups.overdue.length} overdue)` : ''}.` : 'No follow-ups today — keep prospecting!'}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-emerald-300 text-emerald-800" onClick={() => nav('/app/leads')}>View Leads</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => nav('/app/proposals/new')}>
            <PlusCircle className="w-4 h-4 mr-2" /> New Proposal
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(k => {
          const I = k.icon;
          return (
            <Card key={k.label} className="p-5 border-emerald-100 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between">
                <div className={`w-10 h-10 rounded-xl ${k.tone} flex items-center justify-center`}><I className="w-5 h-5" /></div>
                <ArrowUpRight className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="mt-4 text-2xl font-extrabold text-emerald-950">{k.value}</div>
              <div className="text-sm text-slate-500">{k.label}</div>
              <div className="text-xs text-emerald-700 mt-1 font-medium">{k.hint}</div>
            </Card>
          );
        })}
      </div>

      {/* Follow-ups + Site Visits row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Follow-ups Today */}
        <Card className="p-6 border-orange-200 bg-gradient-to-br from-orange-50/50 to-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center"><Calendar className="w-4.5 h-4.5 text-orange-700" /></div>
              <div>
                <h3 className="text-lg font-bold text-emerald-950">Today&apos;s Follow-ups</h3>
                <p className="text-xs text-slate-500">{followups.total} to reach out</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="text-orange-700" onClick={() => nav('/app/pipeline')} data-testid="dashboard-open-pipeline">Pipeline <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
          </div>
          {followups.total === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm italic">No follow-ups due today. You&apos;re all caught up! 🎉</div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {followups.overdue.length > 0 && (
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Overdue ({followups.overdue.length})
                </div>
              )}
              {followups.overdue.map(l => (
                <div key={l.id} className="p-2.5 bg-rose-50 border border-rose-100 rounded-lg flex items-center gap-3" data-testid={`followup-overdue-${l.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-rose-200 flex items-center justify-center font-bold text-rose-800">{l.name[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-emerald-950 truncate">{l.name}</div>
                    <div className="text-[11px] text-slate-500">{l.phone} · {l.kw} kW · {l.city}</div>
                  </div>
                  <Badge className="bg-rose-600 text-white text-[10px] border-0">{fmtDay(l.follow_up_date)}</Badge>
                </div>
              ))}
              {followups.today.length > 0 && (
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-orange-700 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Today ({followups.today.length})
                </div>
              )}
              {followups.today.map(l => (
                <div key={l.id} className="p-2.5 bg-orange-50 border border-orange-100 rounded-lg flex items-center gap-3" data-testid={`followup-today-${l.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-orange-200 flex items-center justify-center font-bold text-orange-800">{l.name[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-emerald-950 truncate">{l.name}</div>
                    <div className="text-[11px] text-slate-500">{l.phone} · {l.kw} kW · {l.city}</div>
                  </div>
                  <a href={`tel:${l.phone}`} className="p-1.5 rounded-full bg-white hover:bg-emerald-50 text-emerald-700" title="Call now"><Phone className="w-4 h-4" /></a>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Upcoming Site Visits */}
        <Card className="p-6 border-teal-200 bg-gradient-to-br from-teal-50/50 to-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center"><Home className="w-4.5 h-4.5 text-teal-700" /></div>
              <div>
                <h3 className="text-lg font-bold text-emerald-950">Upcoming Site Visits</h3>
                <p className="text-xs text-slate-500">{visits.length} scheduled</p>
              </div>
            </div>
          </div>
          {visits.length === 0 ? (
            <div className="py-6 text-center text-slate-400 text-sm italic">No site visits scheduled. Schedule one from a lead card in Pipeline.</div>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {visits.slice(0, 5).map(v => (
                <div key={v.id} className="p-2.5 bg-teal-50 border border-teal-100 rounded-lg" data-testid={`visit-${v.id}`}>
                  <div className="flex items-center gap-2 justify-between">
                    <div className="font-semibold text-sm text-emerald-950 truncate">{v.lead_name || v.lead_id}</div>
                    <Badge className="bg-teal-600 text-white text-[10px] border-0">{fmtDay(v.scheduled_at)}</Badge>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {fmtDateTime(v.scheduled_at)}{v.engineer_name && <>· 👷 {v.engineer_name}</>}</div>
                  {v.address && <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.address}</div>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Funnel Analytics — 3 columns */}
      {funnel && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Conversion Rates */}
          <Card className="p-6 border-emerald-100">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-emerald-700" />
              <div>
                <h3 className="text-lg font-bold text-emerald-950">Conversion Rates</h3>
                <p className="text-xs text-slate-500">Stage-to-stage progression</p>
              </div>
            </div>
            <div className="space-y-2">
              {funnel.conversions.map(c => (
                <div key={`${c.from}-${c.to}`} className="p-2.5 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 truncate mr-2"><span className="font-semibold">{c.from}</span> → <span className="font-semibold">{c.to}</span></span>
                    <span className={`font-bold ${c.pct >= 50 ? 'text-emerald-700' : c.pct >= 25 ? 'text-amber-700' : 'text-rose-700'}`}>{c.pct}%</span>
                  </div>
                  <div className="mt-1.5 h-1.5 bg-white rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${c.pct >= 50 ? 'bg-emerald-500' : c.pct >= 25 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: Math.min(100, c.pct) + '%' }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Rep Leaderboard */}
          <Card className="p-6 border-emerald-100">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-amber-600" />
              <div>
                <h3 className="text-lg font-bold text-emerald-950">Rep Leaderboard</h3>
                <p className="text-xs text-slate-500">Wins &amp; win-rate</p>
              </div>
            </div>
            {funnel.rep_leaderboard.length === 0 ? (
              <div className="py-6 text-center text-slate-400 text-xs italic">No leads assigned yet.</div>
            ) : (
              <div className="space-y-2">
                {funnel.rep_leaderboard.slice(0, 5).map((r, i) => (
                  <div key={r.id} className="p-2.5 bg-gradient-to-r from-amber-50 to-transparent rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-500 text-white' : i === 1 ? 'bg-slate-400 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-slate-200 text-slate-600'}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-emerald-950 truncate">{r.name}</div>
                        <div className="text-[10px] text-slate-500">{r.total} total · {r.won} won · {r.open} open</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-extrabold text-emerald-700">{r.win_pct}%</div>
                        <div className="text-[9px] text-slate-500 uppercase tracking-wide">win rate</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Overall Funnel Metrics */}
          <Card className="p-6 border-emerald-100">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-5 h-5 text-violet-600" />
              <div>
                <h3 className="text-lg font-bold text-emerald-950">Sales Health</h3>
                <p className="text-xs text-slate-500">Overall metrics</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 bg-violet-50 rounded-lg text-center">
                <div className="text-2xl font-extrabold text-violet-800">{funnel.overall_win_pct}%</div>
                <div className="text-[10px] text-violet-700 uppercase tracking-wide font-bold mt-0.5">Win Rate</div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <div className="text-2xl font-extrabold text-emerald-800">{funnel.avg_cycle_days}d</div>
                <div className="text-[10px] text-emerald-700 uppercase tracking-wide font-bold mt-0.5">Avg Cycle</div>
              </div>
              <div className="p-3 bg-sky-50 rounded-lg text-center">
                <div className="text-2xl font-extrabold text-sky-800">{funnel.total_leads}</div>
                <div className="text-[10px] text-sky-700 uppercase tracking-wide font-bold mt-0.5">Total Leads</div>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg text-center">
                <div className="text-2xl font-extrabold text-orange-800">{funnel.stages.find(s => s.stage === 'Won')?.count || 0}</div>
                <div className="text-[10px] text-orange-700 uppercase tracking-wide font-bold mt-0.5">Deals Won</div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="w-full mt-3 border-emerald-200 text-emerald-800" onClick={() => nav('/app/pipeline')}>
              Open Pipeline <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 border-emerald-100">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-lg font-bold text-emerald-950">Recent Leads</h3>
              <p className="text-sm text-slate-500">Latest enquiries from across Karnataka</p>
            </div>
            <Button variant="ghost" className="text-emerald-700" onClick={() => nav('/app/leads')}>View all <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
          <div className="divide-y divide-emerald-50">
            {stats.recentLeads.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-sm">No leads yet. <a className="text-emerald-700 font-semibold cursor-pointer" onClick={() => nav('/app/leads')}>Add one →</a></div>
            ) : stats.recentLeads.map(l => (
              <div key={l.id} className="py-3 flex items-center gap-4 hover:bg-emerald-50/40 rounded-lg px-2 -mx-2 cursor-pointer" onClick={() => nav('/app/proposals/new', { state: { lead: l }})}>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-100 to-amber-100 flex items-center justify-center font-bold text-emerald-800">{l.name[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-emerald-950">{l.name}</div>
                  <div className="text-xs text-slate-500">{l.segment} • {l.kw} kW • {l.city}</div>
                </div>
                <Badge className={`${statusColor[l.status] || 'bg-slate-100 text-slate-700'} hover:bg-inherit border-0 rounded-full`}>{l.status}</Badge>
                <Button size="sm" variant="ghost" className="text-emerald-700">
                  <Sparkles className="w-4 h-4 mr-1" /> Quote
                </Button>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-emerald-100 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-400/20 blur-2xl" />
          <Zap className="w-8 h-8 text-amber-300" />
          <h3 className="mt-4 text-xl font-bold">Quick Proposal</h3>
          <p className="mt-1 text-sm text-emerald-100/80">Generate a customer-ready proposal in 5 steps. Share via WhatsApp on the spot.</p>
          <div className="mt-6 space-y-3">
            {['Lead Info', 'Site & System', 'Components', 'Financials', 'Generate PDF'].map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center text-xs font-bold">{i+1}</div>
                <div className="text-sm">{s}</div>
              </div>
            ))}
          </div>
          <Button className="mt-6 w-full bg-amber-500 hover:bg-amber-600 text-emerald-950 font-semibold" onClick={() => nav('/app/proposals/new')}>
            Start New Proposal <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-emerald-100">
          <h3 className="text-lg font-bold text-emerald-950">Monthly Target</h3>
          <p className="text-sm text-slate-500">Progress across segments</p>
          <div className="mt-5 space-y-4">
            {stats.monthlyTarget.map(r => (
              <div key={r.segment}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium text-emerald-900">{r.segment}</span>
                  <span className="text-slate-500">{inr(r.current)} / {inr(r.target)}</span>
                </div>
                <div className="h-2 bg-emerald-50 rounded-full overflow-hidden">
                  <div className={`h-full ${segmentColor[r.segment] || 'bg-emerald-600'} rounded-full transition-all duration-700`} style={{ width: r.pct + '%' }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-emerald-100">
          <h3 className="text-lg font-bold text-emerald-950">Pipeline Funnel</h3>
          <p className="text-sm text-slate-500">Where your leads stand right now</p>
          <div className="mt-5 space-y-3">
            {stats.funnel.map(r => {
              const w = Math.max(8, Math.round((r.count / totalFunnel) * 100));
              return (
                <div key={r.stage} className="flex items-center gap-3">
                  <div className="w-28 text-sm font-medium text-emerald-900">{r.stage}</div>
                  <div className="flex-1 h-9 bg-emerald-50 rounded-lg overflow-hidden relative">
                    <div className="h-full rounded-lg flex items-center justify-end pr-3 text-xs font-bold text-white transition-all duration-700"
                      style={{ width: w + '%', background: 'linear-gradient(90deg, #10b981, #059669)' }}>
                      {r.count} {r.count === 1 ? 'lead' : 'leads'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
